import { FeaturesReady, GrowthBookProvider } from '@growthbook/growthbook-react';
import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import AppPaths from './AppPaths';
import { ChatEmbed } from './ChatEmbed';
import { LocaleRouteWrapper } from './components/LocaleRouteWrapper';
import { getAxonUserData } from './lib/analytics/events';
import { typedWindow } from './lib/Window';
import SentryRoute from './SentryRoute';
import styles from './Series3App.module.scss';
import AppBar from './components/AppBar';
import AutoAppliedCouponBanner from './components/CouponBanner/AutoAppliedCouponBanner';
import Footer from './components/Footer';
import Loading from './components/Loading';
import ReferralRedirect from './components/ReferralRedirect';
import ReviewsCarousel from './components/ReviewsCarousel';
import HideChatWidget from './components/ZendeskChat/HideChatWidget';
import { Series3ContextProvider, useSeries3Context } from './contexts/Series3Context';
import { PdpImagesProvider, usePdpImagesContext } from './contexts/PdpImagesContext';
import useAnalytics from './hooks/useAnalytics';
import { CartModeProvider, CartModes } from './lib/cartModes';
import { getFiGrowthBook } from './lib/growthbook';
import { FiAppProps } from './lib/router';
import * as types from './types';
import Cart from './views/Cart';
import ConfigReloadRetry from './views/ConfigReloadRetry/ConfigReloadRetry';
import Home from './views/Home';
import ProductDetails from './views/ProductDetails/ProductDetails';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Series3UpgradeContextProvider } from './contexts/Series3UpgradeContext';
import { useSetTenancyFromURL } from './hooks/useTenancy';
import SupplementsEcom from './views/ManageSubscription/Supplements/components/SupplementsEcom/SupplementsEcom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import MiniPDP from './components/MiniPDP/MiniPDP';
import UltraPDP from './components/UltraPDP/UltraPDP';
import Compare from './components/Compare/Compare';
import { HideCookieConsentBanner } from './lib/oneTrust';
import { LocaleContextProvider } from './contexts/LocaleContext';
import ProductListingPage from './components/ProductListingPage/ProductListingPage';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getDomainConfig } from './lib/domainConfig';
import { AwinClickCapture } from './components/AwinTracker';
import { FacebookClickCapture } from './components/FacebookClickCapture';

/**
 * Route-based code splitting: lazy-load views only reached via deep links or in-app navigation.
 * High-traffic storefront pages (Home, ProductDetails, Cart, MiniPDP, UltraPDP, Compare,
 * ProductListingPage, SupplementsEcom) remain statically imported for instant render.
 */
const Accessories = React.lazy(() => import('./views/Accessories'));
const Checkout = React.lazy(() => import('./views/Checkout'));
const ForgotPassword = React.lazy(() => import('./views/ForgotPassword'));
const GiftCard = React.lazy(() => import('./views/GiftCard'));
const Login = React.lazy(() => import('./views/Login'));
const MembershipUpgrade = React.lazy(() => import('./views/MembershipUpgrade/MembershipUpgrade'));
const MonthlyUpgrade = React.lazy(() => import('./views/MonthlyUpgrade/MonthlyUpgrade'));
const PurchaseSubscriptionBlocker = React.lazy(
  () => import('./views/PurchaseSubscription/PurchaseSubscriptionBlocker/PurchaseSubscriptionBlocker'),
);
const PurchaseSubscriptionRoutes = React.lazy(() => import('./views/PurchaseSubscription/PurchaseSubscriptionRoutes'));
const Referrals = React.lazy(() => import('./views/Referrals/Referrals'));
const RescuePlacement = React.lazy(() => import('./views/Rescue/Placement/RescuePlacement/RescuePlacement'));
const Subscription = React.lazy(() => import('./views/ManageSubscription/Collar/Subscription'));
const UpdateBillingInfo = React.lazy(() => import('./views/UpdateBillingInfo/UpdateBillingInfo'));
const Upgrade = React.lazy(() => import('./views/Upgrade'));
const UpgradeV2 = React.lazy(() => import('./views/UpgradeV2/UpgradeV2'));
const Supplements = React.lazy(() => import('./views/ManageSubscription/Supplements/Supplements'));
const Troubleshoot = React.lazy(() => import('./views/Troubleshoot'));
const NotFound = React.lazy(() => import('./views/NotFound'));
const PrepaidActivation = React.lazy(() => import('./views/PrepaidActivation'));
const Airvet = React.lazy(() => import('./views/ManageSubscription/ThirdParty/Airvet'));
const RenewAndSave = React.lazy(() => import('./views/RenewAndSave/RenewAndSave'));
const Packs = React.lazy(() => import('./views/Pack/Pack'));
const WebSubscription = React.lazy(() => import('./views/ManageSubscription/Web/WebSubscription'));
const Series3PlusUpgrade = React.lazy(() => import('./views/SeriesThreePlusUpgrade/SeriesThreePlusUpgrade'));
const UltraUpgrade = React.lazy(() => import('./views/UltraUpgrade/UltraUpgrade'));
const Verizon = React.lazy(() => import('./views/Verizon/Verizon'));

// Bypass barrel file (./views/Rescue/index.ts) to import directly from file with default export
const RescuePurchaseGiftCards = React.lazy(() => import('./views/Rescue/PurchaseGiftCards/PurchaseGiftCards'));

// Named exports need .then() wrapper to re-export as default for React.lazy()
const RedeemSeries3Collar = React.lazy(() =>
  import('./views/ProductDetails/Series3CollarDetails/RedeemSeries3Collar').then((m) => ({
    default: m.RedeemSeries3Collar,
  })),
);
const VipRedeemSeries3Collar = React.lazy(() =>
  import('./views/ProductDetails/Series3CollarDetails/VipRedeemSeries3Collar').then((m) => ({
    default: m.VipRedeemSeries3Collar,
  })),
);
const RedeemMini = React.lazy(() =>
  import('./views/ProductDetails/MiniDetails/RedeemMini').then((m) => ({
    default: m.RedeemMini,
  })),
);
const ProductQuiz = React.lazy(() =>
  import('./components/ProductQuiz/ProductQuiz').then((m) => ({
    default: m.ProductQuiz,
  })),
);

/**
 * Renders hreflang alternate and self-referencing canonical link tags for SEO.
 * During the tryfi.com → fitracking.com migration, both domains serve geo-adaptive
 * content. hreflang tells Google these are regional variants (not duplicates), and
 * the self-referencing canonical asserts each domain's authority for its own pages.
 */
function SeoHelmet() {
  const { pathname, search } = useLocation();
  const { shopDomain, hreflangEntries, xDefaultShopDomain } = getDomainConfig();
  const pathWithSearch = `${pathname}${search}`;

  return (
    <Helmet>
      <link rel="canonical" href={`https://${shopDomain}${pathWithSearch}`} />
      {hreflangEntries.map((entry) => (
        <link
          key={entry.hreflang}
          rel="alternate"
          hrefLang={entry.hreflang}
          href={`https://${entry.shopDomain}${pathWithSearch}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`https://${xDefaultShopDomain}${pathWithSearch}`} />
    </Helmet>
  );
}

const LEGACY_COLLAR_ROUTE = { v1: AppPaths.Products('smart-collar'), v2: AppPaths.Products('smart-collar-v2') };

// This previous password reset path was accidentally changed, but had been published in help articles. This constant
// will be used to redirect to the new route.
const LEGACY_FORGOT_PASSWORD_ROUTE = '/passwordReset';

function PageScrollBehavior() {
  const { hash, pathname, state } = useLocation<{ noScroll?: boolean } | undefined>();

  // react-router does not natively handle scrolling to anchors on route change
  // This solution was borrowed from: https://stackoverflow.com/a/61311926/1480571
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
        }
      }, 100);
      return;
    }

    if (!state?.noScroll) {
      window.scrollTo(0, 0);
    }
  }, [hash, pathname, state?.noScroll]);

  return <React.Fragment />;
}

interface ConfigLoaderProps {
  children: JSX.Element;
  requireLogin?: boolean;
}

function ConfigLoaderInner({ children, requireLogin }: ConfigLoaderProps) {
  const location = useLocation();

  const { appInitialized, initializationError, initializing, retryInitialization } = useSeries3Context();
  const { isLoading: pdpImagesLoading } = usePdpImagesContext();
  const session = useSelector((state: types.AppState) => state.session);
  const { analyticsState } = useAnalytics(location, appInitialized);

  // Check if we're logged in and the component requires us to be logged in
  if (appInitialized && requireLogin && !session) {
    return (
      <Redirect to={`${AppPaths.Login}?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`} />
    );
  }

  // If we're unable to initialize the app, we won't be able to render the site properly so we will show a special
  // component that allows the user to retry fetching the site config from our api.
  if (initializationError) {
    return <ConfigReloadRetry reload={retryInitialization} />;
  }

  // Show loading while app config, analytics, or PDP images are loading
  if (!appInitialized || initializing || analyticsState === 'loading' || pdpImagesLoading) {
    return <Loading />;
  }

  return children;
}

function ConfigLoader({ children, requireLogin }: ConfigLoaderProps) {
  return (
    <PdpImagesProvider>
      <ConfigLoaderInner requireLogin={requireLogin}>{children}</ConfigLoaderInner>
    </PdpImagesProvider>
  );
}

/**
 * Wraps a component with the app context provider, which loads necessary site configuration, and shows a loading
 * throbber until the app is ready to render.
 */
function requireConfig(Component: React.ComponentType<FiAppProps>) {
  return function RequireConfig({ children, requireLogin, upgradeContext }: FiAppProps) {
    return (
      <Series3ContextProvider upgradeFlow={upgradeContext}>
        <ConfigLoader requireLogin={requireLogin}>
          <>
            <Component>{children}</Component>
            {/* PageScrollBehavior manually handles scrolling to anchors for react-router page changes */}
            {/* it should go after config was fetched and page content drawn so the referenced element id exists */}
            <PageScrollBehavior />
          </>
        </ConfigLoader>
      </Series3ContextProvider>
    );
  };
}

function App() {
  useEffect(() => {
    if (typedWindow.axon) {
      typedWindow.axon('track', 'page_view', { user_data: getAxonUserData() });
    }
  }, []);

  useSetTenancyFromURL();

  return (
    <LocaleRouteWrapper>
      <AutoAppliedCouponBanner />
      <>
        <AppBar />
        <ChatEmbed />
      </>
      <Suspense fallback={<Loading />}>
        <Switch>
          <SentryRoute exact path={LEGACY_COLLAR_ROUTE.v1}>
            <Redirect to={AppPaths.Products(types.series3CollarId)} />
          </SentryRoute>
          <SentryRoute exact path={LEGACY_COLLAR_ROUTE.v2}>
            <Redirect to={AppPaths.Products(types.series3CollarId)} />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Products('supplements')}>
            <SupplementsEcom />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Products('fi-mini')}>
            <MiniPDP />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Products('fi-ultra')}>
            <UltraPDP />
          </SentryRoute>
          <SentryRoute path={AppPaths.Products(':id')}>
            {(routeProps: any) => (
              <>
                <ProductDetails />
                {routeProps?.match?.params?.id !== types.series3CollarId &&
                  routeProps?.match?.params?.id !== types.series3PlusCollarId && <ReviewsCarousel />}
                <Footer />
              </>
            )}
          </SentryRoute>
          <SentryRoute path={AppPaths.ApplyReferralCode}>
            <ReferralRedirect />
          </SentryRoute>
          <SentryRoute path={AppPaths.Bag}>
            <Cart />
            <HideChatWidget />
          </SentryRoute>
          <SentryRoute path={AppPaths.Compare}>
            <Compare />
          </SentryRoute>
          <SentryRoute path={AppPaths.Quiz}>
            <HideChatWidget />
            <ProductQuiz />
          </SentryRoute>
          <SentryRoute path={AppPaths.ProductListingPage}>
            <ProductListingPage />
          </SentryRoute>
          <SentryRoute path={AppPaths.Checkout}>
            <Checkout />
            <HideChatWidget />
          </SentryRoute>
          <SentryRoute exact path={LEGACY_FORGOT_PASSWORD_ROUTE}>
            <Redirect to={AppPaths.ForgotPassword} />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.ForgotPassword}>
            <ForgotPassword />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Gift}>
            <GiftCard />
            <Footer />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.GiftFi}>
            <Redirect to={AppPaths.Products(types.series3CollarId)} />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Redeem}>
            <RedeemSeries3Collar />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.MiniRedeem}>
            <RedeemMini />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Vip}>
            <VipRedeemSeries3Collar />
          </SentryRoute>
          <SentryRoute path={AppPaths.Accessories}>
            <Accessories />
            <Footer />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Rescue.PurchaseGiftCards}>
            <RescuePurchaseGiftCards />
            <Footer />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Login}>
            <Login />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.NotFound}>
            <NotFound />
            <Footer />
          </SentryRoute>
          <SentryRoute exact path={AppPaths.Home}>
            <Home />
          </SentryRoute>
        </Switch>
      </Suspense>
    </LocaleRouteWrapper>
  );
}

function Series3UpgradeApp() {
  return (
    <>
      <CartModeProvider mode={CartModes.series3}>
        <HideChatWidget />
        <HideCookieConsentBanner />
        <Upgrade />
      </CartModeProvider>
    </>
  );
}

function Series3UpgradeAppV2() {
  return (
    <>
      <CartModeProvider mode={CartModes.series3UpgradeV2}>
        <Series3UpgradeContextProvider>
          <HideChatWidget />
          <HideCookieConsentBanner />
          <UpgradeV2 />
        </Series3UpgradeContextProvider>
      </CartModeProvider>
    </>
  );
}

function VerizonApp() {
  return (
    <>
      <CartModeProvider mode={CartModes.verizon}>
        <HideChatWidget />
        <HideCookieConsentBanner />
        <Verizon />
      </CartModeProvider>
    </>
  );
}

function UltraUpgradeApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <UltraUpgrade />
    </>
  );
}

function Series3PlusUpgradeApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Series3PlusUpgrade />
    </>
  );
}

function SubscriptionApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Subscription />
    </>
  );
}

function PrepaidActivationApp() {
  return <PrepaidActivation />;
}

function BillingInfoUpdateApp() {
  return <UpdateBillingInfo />;
}

function SupplementsApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <>
          <Supplements />
        </>
      </LocalizationProvider>
    </>
  );
}

function MembershipUpgradeApp() {
  return (
    <>
      <MembershipUpgrade />
    </>
  );
}

function MonthlyUpgradeApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <MonthlyUpgrade />
    </>
  );
}

function InAppSubscriptionPurchaseApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <PurchaseSubscriptionRoutes />
    </>
  );
}

function InAppSubscriptionPurchaseBlockerApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <PurchaseSubscriptionBlocker />
    </>
  );
}

function ReferralsApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Referrals />
    </>
  );
}

function RescuePlacementApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <RescuePlacement />
    </>
  );
}

function TroubleshootApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Troubleshoot />
    </>
  );
}

function AirvetManagementApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Airvet />
    </>
  );
}

function RenewAndSaveApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <RenewAndSave />
    </>
  );
}

function PacksApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <Packs />
    </>
  );
}

function WebSubscriptionApp() {
  return (
    <>
      <HideChatWidget />
      <HideCookieConsentBanner />
      <WebSubscription />
    </>
  );
}

// Wrapping with the RUM HOC first seems to fix an issue where the shipping page would update the session store after
// creating a new user and then would stop processing the rest of the callbacks which update the address.
const ConfiguredApp = requireConfig(App);
const ConfiguredSeries3UpgradeApp = requireConfig(Series3UpgradeApp);
const ConfigureSeries3UpgradeAppV2 = requireConfig(Series3UpgradeAppV2);
const ConfiguredUltraUpgradeApp = requireConfig(UltraUpgradeApp);
const ConfiguredVerizonApp = requireConfig(VerizonApp);
const ConfigureSeries3PlusUpgradeApp = requireConfig(Series3PlusUpgradeApp);
const ConfiguredMembershipUpgradeApp = requireConfig(MembershipUpgradeApp);
const ConfiguredMonthlyUpgradeApp = requireConfig(MonthlyUpgradeApp);
const ConfiguredSubscriptionApp = requireConfig(SubscriptionApp);
const ConfiguredPrepaidActivationApp = requireConfig(PrepaidActivationApp);
const ConfiguredSupplementsApp = requireConfig(SupplementsApp);
const ConfiguredBillingInfoUpdateApp = requireConfig(BillingInfoUpdateApp);
const ConfiguredReferralsApp = requireConfig(ReferralsApp);
const ConfiguredInAppSubscriptionPurchaseApp = requireConfig(InAppSubscriptionPurchaseApp);
const ConfiguredInAppSubscriptionPurchaseBlockerApp = requireConfig(InAppSubscriptionPurchaseBlockerApp);
const ConfiguredRescuePlacementApp = requireConfig(RescuePlacementApp);
const ConfiguredTroubleshootApp = requireConfig(TroubleshootApp);
const ConfiguredAirvetApp = requireConfig(AirvetManagementApp);
const ConfiguredRenewAndSaveApp = requireConfig(RenewAndSaveApp);
const ConfiguredPacksApp = requireConfig(PacksApp);
const ConfiguredWebSubscriptionApp = requireConfig(WebSubscriptionApp);

function Series3AppRouter() {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <SentryRoute exact path={AppPaths.PurchaseSubscription.Root}>
          <ConfiguredInAppSubscriptionPurchaseBlockerApp />
        </SentryRoute>
        <SentryRoute path={AppPaths.PurchaseSubscription.PlanSelection()}>
          <ConfiguredInAppSubscriptionPurchaseApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.GiftFiActivation.Root()}>
          <ConfiguredPrepaidActivationApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.PrepaidActivation.Root()}>
          <ConfiguredPrepaidActivationApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.Series3Upgrade.Root}>
          <ConfiguredSeries3UpgradeApp requireLogin upgradeContext />
        </SentryRoute>
        <SentryRoute path={AppPaths.Series3UpgradeV2.Root}>
          <ConfigureSeries3UpgradeAppV2 requireLogin upgradeContext />
        </SentryRoute>
        <SentryRoute path={AppPaths.UltraUpgrade.Root}>
          <ConfiguredUltraUpgradeApp requireLogin upgradeContext />
        </SentryRoute>
        <SentryRoute path={AppPaths.Verizon.Root}>
          <ConfiguredVerizonApp />
        </SentryRoute>
        <SentryRoute path={AppPaths.Series3PlusUpgrade.PlanSelection()}>
          <ConfigureSeries3PlusUpgradeApp requireLogin upgradeContext />
        </SentryRoute>
        <SentryRoute path={AppPaths.MembershipUpgrade.PlanSelection()}>
          <ConfiguredMembershipUpgradeApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.RenewAndSave.PlanSelection()}>
          <ConfiguredRenewAndSaveApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.Packs.Root}>
          <ConfiguredPacksApp requireLogin />
        </SentryRoute>
        <SentryRoute path={AppPaths.MonthlyUpgrade.PlanSelection()}>
          <ConfiguredMonthlyUpgradeApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.Subscription.SplashPageForDevice()}>
          <ConfiguredSubscriptionApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.Supplements.Root}>
          <ConfiguredSupplementsApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.BillingInfoUpdate.Update}>
          <ConfiguredBillingInfoUpdateApp />
        </SentryRoute>

        <SentryRoute exact path={AppPaths.Referrals}>
          <ConfiguredReferralsApp requireLogin />
        </SentryRoute>

        <SentryRoute exact path={AppPaths.Rescue.Placement}>
          <ConfiguredRescuePlacementApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.Troubleshoot.Start()}>
          <ConfiguredTroubleshootApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.Airvet.Root}>
          <ConfiguredAirvetApp requireLogin />
        </SentryRoute>

        <SentryRoute path={AppPaths.WebSubscription.Root}>
          <ConfiguredWebSubscriptionApp requireLogin />
        </SentryRoute>

        <SentryRoute>
          <ConfiguredApp />
        </SentryRoute>
      </Switch>
    </Suspense>
  );
}

export default function Series3App() {
  useEffect(() => {
    void getFiGrowthBook().setAttributes();
    getFiGrowthBook().loadFeatures();
  }, []);

  const { t } = useTranslation();

  return (
    <>
      <GrowthBookProvider growthbook={getFiGrowthBook().growthBook}>
        <FeaturesReady>
          <AwinClickCapture />
          <FacebookClickCapture />
          <MantineProvider
            withGlobalStyles
            withNormalizeCSS
            theme={{
              fontFamily: 'MessinaSans, sans-serif',
              headings: { fontFamily: 'MessinaSans-Compact, sans-serif' },
              colors: {
                'fi-yellow': [
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                  '#FFFA01',
                ],
                'background-dark-primary': [
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                ],
                'background-dark-secondary': [
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                  '#242424',
                ],
                'background-dark-tertiary': [
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                  '#3C3C3C',
                ],
                'background-light-secondary': [
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                  '#F3F3F2',
                ],
                'background-light-tertiary': [
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                  '#E5E5E4',
                ],
                'type-dark-primary': [
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                  '#FFFFFF',
                ],
                'type-dark-secondary': [
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                  '#9E9E9C',
                ],
                'type-light-primary': [
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                  '#000000',
                ],
                'type-light-secondary': [
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                ],
                'stroke-dark-border': [
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                  '#737373',
                ],
                'gray-300': ['#9E9E9C'],
                'gray-400': ['#737373'],
              },
            }}
          >
            <Helmet>
              <title>{t('store_title')}</title>
            </Helmet>
            <SeoHelmet />
            <Notifications position="bottom-center" />
            <div className={styles.container}>
              <LocaleContextProvider>
                <Series3AppRouter />
              </LocaleContextProvider>
            </div>
            {/* When popup-root is defined, popupjs-model will put modals inside it. We want it close to <body /> root */}
            <div id="popup-root" />
          </MantineProvider>
        </FeaturesReady>
      </GrowthBookProvider>
    </>
  );
}
